import { MediaCollection } from "./types";
import { GeneratedSkedApiClient } from "../generated-client";
import type { paths } from "../generated/sked-api-openapi-types";

type StartFrontifySyncOperation =
  paths["/content-inbox/inbox/{inboxId}/sync-frontify-assets"]["post"];
type StartFrontifySyncResponse =
  StartFrontifySyncOperation["responses"][200]["content"]["application/json"];
type GetFrontifySyncStatusOperation =
  paths["/content-inbox/inbox/{inboxId}/sync-status"]["get"];
type GetFrontifySyncStatusResponse =
  GetFrontifySyncStatusOperation["responses"][200]["content"]["application/json"];
type DuplicateInboxAssetsOperation =
  paths["/content-inbox/inbox/{sourceInboxId}/duplicate-to/{destinationInboxId}/assets"]["post"];
type DuplicateInboxAssetsBody = NonNullable<
  DuplicateInboxAssetsOperation["requestBody"]
>["content"]["application/json"];
type DuplicateInboxAssetsResponse =
  DuplicateInboxAssetsOperation["responses"][200]["content"]["application/json"];

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();

export class FrontifySyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FrontifySyncError";
  }
}

export const syncCollection = async (
  collection: MediaCollection
): Promise<StartFrontifySyncResponse> => {
  try {
    const result = await generatedSkedApiClient.client.POST(
      "/content-inbox/inbox/{inboxId}/sync-frontify-assets",
      {
        params: {
          path: { inboxId: collection._id },
        },
      }
    );
    return await generatedSkedApiClient.unwrap(result);
  } catch (error) {
    throw new FrontifySyncError(
      `Failed to sync Frontify collection: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};

export const getCollectionSyncStatus = async (
  collection: MediaCollection
): Promise<GetFrontifySyncStatusResponse> => {
  try {
    const result = await generatedSkedApiClient.client.GET(
      "/content-inbox/inbox/{inboxId}/sync-status",
      {
        params: {
          path: { inboxId: collection._id },
        },
      }
    );
    return await generatedSkedApiClient.unwrap(result);
  } catch (error) {
    throw new FrontifySyncError(
      `Failed to get sync status: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};
export const duplicateCollectionAssets = async (
  sourceCollectionId: string,
  destinationCollectionId: string,
  assetIds: string[]
): Promise<DuplicateInboxAssetsResponse> => {
  try {
    const result = await generatedSkedApiClient.client.POST(
      "/content-inbox/inbox/{sourceInboxId}/duplicate-to/{destinationInboxId}/assets",
      {
        params: {
          path: {
            sourceInboxId: sourceCollectionId,
            destinationInboxId: destinationCollectionId,
          },
        },
        body: { assetIds } satisfies DuplicateInboxAssetsBody,
      }
    );
    return await generatedSkedApiClient.unwrap(result);
  } catch (error) {
    throw new FrontifySyncError(
      `Failed to duplicate assets: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};

const getFrontifySyncStatus = async (
  collection: MediaCollection,
  timeoutMs = 300000, // 5 minute timeout by default
  intervalMs = 2000
): Promise<boolean> => {
  const startTime = Date.now();

  const poll = async (): Promise<boolean> => {
    if (Date.now() - startTime > timeoutMs) {
      throw new FrontifySyncError("Sync status polling timed out");
    }

    const status = await getCollectionSyncStatus(collection);

    if (!status.isSyncing) {
      return true;
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    return poll();
  };

  return poll();
};

export async function frontifySyncer(
  collection: MediaCollection,
  timeoutMs?: number
) {
  try {
    await syncCollection(collection);
    await getFrontifySyncStatus(collection, timeoutMs);
  } catch (error) {
    if (error instanceof FrontifySyncError) {
      throw error;
    }
    throw new FrontifySyncError(
      `Frontify sync failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}
